export interface Product {
  id: string;
  name: string;
  englishName: string;
  hindiName: string;
  botanicalName: string;
  image: string;
  description: string;
  benefits: string[]; // short list used on card (up to 3 shown)
  ayurvedicProperties: {
    rasa: string;
    virya: string;
    vipaka: string;
    doshaEffect: string;
  };
  usesAndBenefits: string[]; // full list used on product detail page
}

const products: Product[] = [
  {
    id: 'amla',
    name: 'Amla',
    englishName: 'Indian Gooseberry',
    hindiName: 'आंवला',
    botanicalName: 'Emblica officinalis / Phyllanthus emblica',
    image: '/images/products/amla.jpg',
    description:
      'Amla, also known as Indian Gooseberry, is one of the most important herbs in Ayurveda. It\'s considered a divine fruit and is revered for its exceptional nutritional and medicinal properties. The fruit is rich in Vitamin C, antioxidants, and various other nutrients that make it a powerful rejuvenative herb. In Ayurveda, Amla is classified as a Rasayana (rejuvenative) herb that promotes longevity and vitality. It\'s particularly known for its cooling properties and its ability to balance all three doshas, especially Pitta. The fruit is commonly used in various Ayurvedic formulations like Chyawanprash and Triphala, and is also consumed fresh, dried, or in powder form.',
    benefits: [
      'Rich source of Vitamin C',
      'Boosts immunity',
      'Promotes digestion',
    ],
    ayurvedicProperties: {
      rasa: 'Sour (Amla), also slightly sweet, bitter, pungent, and astringent',
      virya: 'Cooling',
      vipaka: 'Sweet',
      doshaEffect: 'Balances all three – Vata, Pitta, and Kapha (especially Pitta)',
    },
    usesAndBenefits: [
      'Rich source of Vitamin C and antioxidants',
      'Boosts immunity, rejuvenates tissues',
      'Promotes digestion and detoxification',
      'Supports skin, hair, and eye health',
      'Commonly used in Chyawanprash and Triphala',
    ],
  },
  {
    id: 'ashwagandha',
    name: 'Ashwagandha',
    englishName: 'Winter Cherry / Indian Ginseng',
    hindiName: 'अश्वगंधा',
    botanicalName: 'Withania somnifera',
    image: '/images/products/ashwagandha.png',
    description:
      'Ashwagandha, often referred to as Indian Ginseng, is one of the most revered herbs in Ayurveda. Known for its adaptogenic properties, it helps the body adapt to stress and maintain homeostasis. The herb is particularly valued for its ability to enhance physical and mental stamina, improve sleep quality, and support the nervous system. In Ayurveda, Ashwagandha is classified as a Rasayana (rejuvenative) herb that promotes longevity and vitality. It\'s especially beneficial for those experiencing stress, fatigue, or weakness, and is commonly used in various Ayurvedic formulations to enhance overall well-being.',
    benefits: [
      'Manages stress and anxiety',
      'Improves strength and stamina',
      'Supports male fertility',
    ],
    ayurvedicProperties: {
      rasa: 'Bitter, astringent, sweet',
      virya: 'Heating',
      vipaka: 'Sweet',
      doshaEffect: 'Balances Vata and Kapha',
    },
    usesAndBenefits: [
      'Adaptogen – helps manage stress and anxiety',
      'Improves strength, stamina, and energy',
      'Supports male fertility and testosterone',
      'Nourishes the nervous system',
      "Often called 'Indian Ginseng'",
    ],
  },
  {
    id: 'shatavari',
    name: 'Shatavari',
    englishName: 'Wild Asparagus / Indian Asparagus',
    hindiName: 'शतावरी',
    botanicalName: 'Asparagus racemosus',
    image: '/images/products/shatavari.png',
    description:
      "Shatavari, known as the 'Queen of Herbs' in Ayurveda, is a powerful female reproductive tonic. The herb is particularly valued for its ability to support women's health throughout various life stages, from puberty to menopause. It's known for its cooling properties and its ability to nourish and strengthen the reproductive system. Shatavari is also beneficial for promoting lactation in breastfeeding mothers and supporting hormonal balance. In Ayurveda, it's classified as a Rasayana (rejuvenative) herb that promotes vitality and longevity.",
    benefits: [
      'Female reproductive tonic',
      'Enhances fertility',
      'Supports lactation',
    ],
    ayurvedicProperties: {
      rasa: 'Sweet, bitter',
      virya: 'Cooling',
      vipaka: 'Sweet',
      doshaEffect: 'Balances Vata and Pitta',
    },
    usesAndBenefits: [
      'Female reproductive tonic',
      'Enhances fertility, balances hormones',
      'Supports lactation in breastfeeding women',
      'Calms the mind, supports digestion',
    ],
  },
  {
    id: 'bala',
    name: 'Bala',
    englishName: 'Country Mallow / Flannel Weed',
    hindiName: 'बला',
    botanicalName: 'Sida cordifolia',
    image: '/images/products/bala-from-web.jpg',
    description:
      "Bala, meaning 'strength' in Sanskrit, is a powerful rejuvenative herb in Ayurveda. It's particularly known for its ability to strengthen muscles, nerves, and bones. The herb is classified as a Rasayana (rejuvenative) and is especially beneficial for those experiencing weakness, fatigue, or debility. Bala is also valued for its ability to support joint health and promote weight gain in undernourished individuals. In Ayurveda, it's commonly used in various formulations, including medicated oils for external application.",
    benefits: [
      'Strengthens muscles and nerves',
      'Rejuvenative properties',
      'Supports joint health',
    ],
    ayurvedicProperties: {
      rasa: 'Sweet',
      virya: 'Cooling',
      vipaka: 'Sweet',
      doshaEffect: 'Balances Vata and Pitta',
    },
    usesAndBenefits: [
      'Strengthens muscles, nerves, and bones',
      'Rejuvenative (Rasayana)',
      'Promotes weight gain in undernourished',
      'Supports arthritis and joint health',
      'Often used in medicated oils for external application',
    ],
  },
  {
    id: 'safed-musli',
    name: 'Safed Musli',
    englishName: 'White Musli / Chlorophytum',
    hindiName: 'सफेद मूसली',
    botanicalName: 'Chlorophytum borivilianum',
    image: '/images/products/safed-musli.png',
    description:
      "Safed Musli, also known as White Musli, is a highly valued herb in Ayurveda for its adaptogenic and aphrodisiac properties. The herb is particularly known for its ability to enhance physical strength, stamina, and vitality. It's classified as a Rasayana (rejuvenative) herb and is especially beneficial for supporting male reproductive health. Safed Musli is also valued for its ability to boost immunity and promote overall well-being. In Ayurveda, it's commonly used in various formulations, including Musli Pak, to enhance vitality and strength.",
    benefits: [
      'Aphrodisiac properties',
      'Boosts strength and stamina',
      'Improves male fertility',
    ],
    ayurvedicProperties: {
      rasa: 'Sweet',
      virya: 'Cooling',
      vipaka: 'Sweet',
      doshaEffect: 'Balances Vata and Pitta',
    },
    usesAndBenefits: [
      'Aphrodisiac and adaptogen',
      'Boosts strength, stamina, immunity',
      'Improves male fertility, testosterone',
      'Rejuvenates the reproductive system',
      'Often used in formulations like Musli Pak',
    ],
  },
  {
    id: 'kala-musli',
    name: 'Kala Musli',
    englishName: 'Black Musli / Curculigo',
    hindiName: 'काली मूसली',
    botanicalName: 'Curculigo orchioides',
    image: '/images/products/kala-musli.png',
    description:
      "Kala Musli, also known as Black Musli, is a powerful herb in Ayurveda known for its aphrodisiac and strength-enhancing properties. The herb is particularly valued for its ability to improve male vitality and support reproductive health. It's classified as a Rasayana (rejuvenative) herb and is especially beneficial for enhancing physical strength and stamina. Kala Musli is also known for its ability to support liver health and promote digestion. In Ayurveda, it's commonly used in various strengthening formulations.",
    benefits: [
      'Aphrodisiac properties',
      'Improves male vitality',
      'Supports liver health',
    ],
    ayurvedicProperties: {
      rasa: 'Bitter, astringent',
      virya: 'Heating',
      vipaka: 'Pungent',
      doshaEffect: 'Primarily balances Vata and Kapha',
    },
    usesAndBenefits: [
      'Aphrodisiac and strength enhancer',
      'Improves male vitality and immunity',
      'Supports liver health and digestion',
      'Used in strengthening formulations',
    ],
  },
  {
    id: 'vidarikand',
    name: 'Vidarikand',
    englishName: 'Indian Kudzu / Pueraria Tuberosa',
    hindiName: 'विदारीकंद',
    botanicalName: 'Pueraria tuberosa',
    image: '/images/products/vidarikand-from-web.jpg',
    description:
      "Vidarikand, also known as Indian Kudzu, is a highly valued herb in Ayurveda for its rejuvenative properties. The herb is particularly known for its ability to nourish and strengthen the reproductive tissues. It's classified as a Rasayana (rejuvenative) herb and is especially beneficial for promoting lactation in women and supporting reproductive health. Vidarikand is also valued for its ability to strengthen the body and boost immunity. In Ayurveda, it's commonly used in various ghee-based formulations as part of Rasayana therapy.",
    benefits: [
      'Rejuvenates reproductive tissues',
      'Promotes lactation',
      'Boosts immunity',
    ],
    ayurvedicProperties: {
      rasa: 'Sweet',
      virya: 'Cooling',
      vipaka: 'Sweet',
      doshaEffect: 'Balances Vata and Pitta',
    },
    usesAndBenefits: [
      'Rejuvenative, especially for reproductive tissues',
      'Promotes lactation in women',
      'Strengthens the body and boosts immunity',
      'Used in Rasayana (anti-aging) therapy',
      'Useful in Ayurvedic ghee-based formulations',
    ],
  },
];

export default products;
